/**
 * The write freeze — a read-preserving, create-blocking tenant state.
 *
 * Manual billing mode's primary lever (decision 4). A non-paying tenant keeps
 * **full read access to everything it owns** and may still EDIT existing files;
 * it cannot add new ones. Plan + decisions:
 * docs/plans/2026-08-06_MANUAL_BILLING_MODE.md §4.
 *
 * This module is the shared vocabulary — the error, the exemptions, and the
 * customer-facing sentence. The state itself is resolved by
 * `isTenantWriteFrozen` in `@papan/database` (it needs the plan ladder and the
 * deployment's `plan_authority`); the GATE is `repos.files.create`.
 *
 * ── Why the gate is at `files.create` and not in `TreeStorage` ───────────
 *
 * An audit of all nine drive-write paths (that doc's §4) found `TreeStorage`
 * misses five of them — including all three cross-tenant share-landing paths,
 * which are exactly the ones decision 10 says must keep working. Every create in
 * the product converges one level lower, on `repos.files.create`, whose own
 * docblock already calls it the universal create chokepoint. Putting the gate
 * there also makes the new-vs-edit distinction free: reaching it IS the create,
 * and every edit path (`writeById`, `restoreVersion`, the collab saves) touches
 * only `versions` and `content_blobs`.
 */

/**
 * Why a create is allowed to proceed in a frozen tenant.
 *
 * ── Fail-closed by construction ─────────────────────────────────────────
 *
 * There is no "unknown origin" member and no default. A create with no exemption
 * is REFUSED, so a new write path added later is gated automatically and its
 * author has to think about it. That is deliberately the opposite of an
 * origin-taxonomy design, where a call site that forgot to pass its origin gets
 * whatever the fallback happens to permit.
 */
export type WriteFreezeExemption =
  /**
   * An inbound cross-tenant share landing (decision 10). The `.shared` row, the
   * `Shared`/`_shared` root that holds it, and the `<SourceOrg>` folder beneath.
   *
   * Refusing these would punish the wrong tenant: the SHARER is a paid-up
   * customer whose user would see a failure caused by somebody else's invoice.
   * And it buys nothing — a `.shared` is a cross-tenant reference, so the bytes
   * never enter the frozen tenant's storage.
   *
   * NOT for cross-tenant COPY, which really does write bytes into the recipient
   * and is therefore refused like any other new item.
   */
  | { reason: 'inbound-share' }
  /**
   * Structural drive scaffolding minted by the platform on someone's behalf, not
   * content the tenant chose to add — the `Shared`/`Library`/`Projects` roots and
   * a new member's `Persons/<email>` folder on invite accept.
   *
   * Decided rather than discovered: a member who joins a frozen organization must
   * end up with a working drive, or the freeze needs a repair pass when it lifts.
   */
  | { reason: 'provisioning' }
  /**
   * A companion row created as part of an EDIT the freeze permits — today only
   * the `.pmeta` sidecar that a document save writes on first save.
   *
   * This is the one carve-out the audit found necessary rather than convenient:
   * saving an existing document performs a `files.create`, so a gate that refused
   * every insert would break document editing for every file that has not got a
   * sidecar yet. That is precisely the strict-direction failure decision 5 exists
   * to avoid.
   *
   * `companionOf` is the id of the file being edited, and the gate VERIFIES it
   * names a real row in the same tenant. That is what keeps this from being a
   * bare trust flag: the exemption is only usable to attach a companion to
   * something that already exists, so it cannot be used to grow a frozen tenant.
   */
  | { reason: 'companion-of-edit'; companionOf: string }
  /**
   * #801 split move (owner decision 2026-09-25): a move whose protected branches stay
   * behind creates ONE folder at the destination to receive the rest. To the user it is
   * a move, not new content. VERIFIED: `splitOf` must name a real FOLDER in the same
   * tenant. Bound: one empty (0-byte) folder per split move that moves ≥1 existing item
   * — `moveSplitting` refuses a split with nothing to move.
   */
  | { reason: 'split-move-shell'; splitOf: string }
  /**
   * #1424 — the platform's own migration of an app's SCHEMA layout (`_db/schema.sql` → `_schemas/main.sql`, the worker's
   * boot sweep `migrateSchemaLayout`) creates ONE empty `_schemas/` folder in the app's folder. Not content the tenant
   * chose to add: the app's existing schema file is MOVED into it. Refusing it in a frozen tenant would leave the app on
   * the old layout, which the next release no longer reads — and the tenant could not create the folder itself either.
   * VERIFIED: the created row is an empty folder named `_schemas` whose parent is `appFolder`, which still holds the
   * `_db/schema.sql` the folder is created to receive (a `.pdapp` or a Mode A folder). Bound: one empty folder per
   * folder with a legacy schema.
   */
  | { reason: 'app-layout-migration'; appFolder: string }
  /**
   * #1544 (owner, 2026-10-31) — an app's `settings.json` (this copy's values) is CREATED the first time one is saved:
   * by a person in the app's Settings, or by the platform's boot sweep that moves the values out of `env.json` /
   * `triggers.json`. Before #1544 the same Settings save OVERWROTE `triggers.json` — which the freeze never refuses — so a
   * paused tenant must still be able to switch a misfiring trigger off. ONLY those two writers pass it (the Settings
   * writer `writeAppControlFile`, the sweep `migrateAppSettings`): never the agent, a token, an upload or an app script —
   * else a create → move out → edit loop mints new items while paused (review round 2). VERIFIED: the created row is a
   * FILE named `settings.json`, ≤ `MAX_APP_SETTINGS_FILE_BYTES`, directly in a control folder (`APP_CONTROL_DIRS`) of
   * `appFolder`, a `.pdapp`. Bound: one per existing app per Settings save.
   */
  | { reason: 'app-settings'; appFolder: string }

/**
 * Does this plan's `features` blob freeze the drive?
 *
 * **The ONE implementation of the `=== false` rule**, and it exists as a function
 * rather than an inline comparison because there are now several readers:
 * `isTenantWriteFrozen` (the gate's answer, per tenant), the console's overdue
 * queue, and the customers list — all of which must not disagree with what the
 * gate is enforcing.
 *
 * ── Why not `!features.canCreateContent` ────────────────────────────────
 *
 * The key is ABSENT on every plan row that predates it, and `!undefined` is
 * `true` — so the negation form freezes every unconfigured plan on every
 * deployment the moment it ships. It is the same class of bug as the `null` ≠ `0`
 * cap mistake this codebase documents, in boolean clothing, and it is the first
 * mutation the freeze's own test suites check for. Absent means unrestricted,
 * matching the `features.models` convention beside it.
 *
 * Takes the blob rather than a boolean so a caller cannot accidentally pass
 * `!!features.canCreateContent` and re-introduce the trap at the call site.
 */
export function planBlocksCreate(
  features: { canCreateContent?: boolean } | null | undefined,
): boolean {
  return features?.canCreateContent === false
}

/** Machine-readable code on the wire. The SPA branches on this, never on copy. */
export const TENANT_WRITE_FROZEN_CODE = 'TENANT_WRITE_FROZEN'

/**
 * The customer-facing sentence, shared by the API refusal and the SPA.
 *
 * ── Decision 8: explain, no contact detail ──────────────────────────────
 *
 * It says what happened, what still works, and what fixes it — and deliberately
 * names no email address, no invoice number and no person. A platform admin
 * confirmed the payment out of band, so any contact detail this text could
 * invent would be wrong for some deployment.
 *
 * ── English-only, and that is a constraint not an oversight ─────────────
 *
 * This renders at a SERVER-side refusal, and server-rendered copy in this
 * codebase cannot use i18n — `@papan/i18n` is browser-only (React peer dep) and
 * notification/email copy is already English-regardless for the same reason.
 * Fixing that properly is one cross-cutting slice affecting every notification
 * type and is explicitly not this feature's job. A localized SPA should render
 * its own string from `TENANT_WRITE_FROZEN_CODE` rather than printing this one.
 */
export function writeFrozenMessage(): string {
  return 'Uploads are paused for this organization. '
    + 'Existing files stay fully available and can still be edited, '
    + 'but new files and folders cannot be created until payment is settled.'
}

/**
 * Thrown by `repos.files.create` when a frozen tenant attempts an unexempted
 * create.
 *
 * ── Matched by `name`, so the name is load-bearing ──────────────────────
 *
 * `apps/api/src/middleware/error.ts` maps this to **402** with
 * `TENANT_WRITE_FROZEN` by checking `err.name`, which is the pattern already
 * used there for `MaxFolderDepthError` and `BrandAssetError` — specifically so
 * the middleware need not import the throwing package. Renaming this class
 * silently turns every refused upload into an opaque 500. `error.ts` states the
 * coupling from its side too.
 *
 * 402 rather than 403 because budget exhaustion already refuses with 402 plus a
 * concrete `code`, and the SPA treats 402 as its money-problem branch — "settle
 * up and this works" is the accurate signal.
 *
 * **Not every surface reaches that middleware.** Collab and the agent/mini-app
 * sync path are WebSocket and never pass through `onError`, so they handle this
 * explicitly at their own boundaries.
 */
export class TenantWriteFrozenError extends Error {
  readonly code = TENANT_WRITE_FROZEN_CODE
  constructor(message: string = writeFrozenMessage()) {
    super(message)
    // Set explicitly: subclassing Error does not reliably carry the constructor
    // name through transpilation, and the central handler matches on it.
    this.name = 'TenantWriteFrozenError'
  }
}
